/**
 * Field drafts — Renata writes into the open form field, not raw JSON in chat.
 * Protocol in assistant output:
 * <FIELD_DRAFT field="description">texto propuesto</FIELD_DRAFT>
 */

export type RenataFieldDraft = {
  field: string;
  value: string;
};

const FIELD_DRAFT_RE =
  /<FIELD_DRAFT\s+field=["']([^"']+)["']\s*>([\s\S]*?)<\/FIELD_DRAFT>/i;
const FIELD_DRAFT_BARE_RE =
  /<FIELD_DRAFT\s+field=([a-zA-Z0-9_]+)\s*>([\s\S]*?)<\/FIELD_DRAFT>/i;

/** Also catch common model mistakes around PROPOSALS blocks. */
const PROPOSALS_BLOCK_RE =
  /<\s*PROPOSALS?\s*>[\s\S]*?<\s*\/\s*PROPOSALS?\s*>/gi;

const LOOSE_PROPOSALS_JSON_RE =
  /<\s*PROPOSALS?\s*>[\s\S]*?(?=(?:Tu siguiente paso|$))/gi;

const META_LINE_RE =
  /^(estoy recordando|tu siguiente paso|mi siguiente paso|puedo ayudarte|si deseas|si quer[eé]s|revis[aá] las acciones|listo:?\s*$|la descripci[oó]n propuesta es:?|pod[eé]s revisarla|puedes revisarla)/i;

const FIELD_LABELS_ES: Record<string, string> = {
  description: "descripción",
  longDescription: "descripción larga",
  name: "nombre",
  descripcion: "descripción",
  descripcion_nueva: "descripción",
  descripcion_larga: "descripción larga",
  long_description: "descripción larga",
  seoTitle: "título para Google",
  seoDescription: "descripción para Google",
  seoMetaTitle: "título para Google",
  seoMetaDescription: "descripción para Google",
};

/** Human label for UI — never show raw keys like longDescription. */
export function fieldLabelEs(field: string): string {
  return FIELD_LABELS_ES[field] ?? "texto del formulario";
}

export function parseFieldDraft(content: string): RenataFieldDraft | null {
  const m = content.match(FIELD_DRAFT_RE) ?? content.match(FIELD_DRAFT_BARE_RE);
  if (!m) return null;
  const field = m[1].trim();
  const cleaned = cleanFieldDraftValue(m[2]);
  if (!field || !cleaned) return null;
  return { field, value: cleaned };
}

export function stripFieldDraft(content: string): string {
  return content.replace(FIELD_DRAFT_RE, "").replace(FIELD_DRAFT_BARE_RE, "").trim();
}

export function stripProposalMarkup(content: string): string {
  let out = content.replace(PROPOSALS_BLOCK_RE, "");
  if (/<\s*PROPOSALS?\s*>/i.test(out)) {
    out = out.replace(LOOSE_PROPOSALS_JSON_RE, "");
    out = out.replace(/<\s*PROPOSALS?\s*>[\s\S]*/gi, "");
  }
  return out.replace(/\n{3,}/g, "\n\n").trim();
}

export function extractProposalsJson(content: string): { text: string; json: string | null } {
  const m = content.match(/<\s*PROPOSALS?\s*>([\s\S]*?)<\s*\/\s*PROPOSALS?\s*>/i);
  if (m) {
    return {
      text: stripProposalMarkup(content.replace(m[0], "")),
      json: m[1].trim(),
    };
  }
  const open = content.match(/<\s*PROPOSALS?\s*>([\s\S]*)/i);
  if (open) {
    const body = open[1];
    const arr = body.match(/\[[\s\S]*\]/);
    return {
      text: stripProposalMarkup(content),
      json: arr ? arr[0].trim() : null,
    };
  }
  return { text: content, json: null };
}

const FIELD_INTENT_RE =
  /write_|create_|improve_|rewrite|expand|summarize|translate|descripcion|description/;

export function wantsFieldTextEdit(opts: {
  userMessage: string;
  field?: string | null;
  intent?: string | null;
}): boolean {
  const intent = (opts.intent ?? "").toLowerCase();
  if (intent && FIELD_INTENT_RE.test(intent)) return true;
  const msg = opts.userMessage.toLowerCase();
  const asksText =
    /descripci[oó]n|mejor(ar|á|a)?\b|reescrib|ampli|escrib|gener(ar|á)|redact|cre(ar|á)|hac[eé]\s+la\b|completar\s+el\s+texto/.test(
      msg,
    );
  if (opts.field && asksText) return true;
  return /descripci[oó]n/.test(msg) && asksText;
}

function looksLikeFieldChrome(text: string): boolean {
  return (
    /^estás editando el campo/i.test(text) ||
    /campo\s+`?(longDescription|description|name|descripcion_larga)`?/i.test(text) ||
    /la descripci[oó]n propuesta es/i.test(text) ||
    /aplicar en el campo/i.test(text) ||
    /tu siguiente paso|mi siguiente paso/i.test(text) ||
    /^listo\.?\s*prepar[eé]/i.test(text) ||
    /FIELD_DRAFT/i.test(text)
  );
}

/** Local models often dump a work plan instead of catalog copy. */
export function looksLikeMetaPlanning(text: string): boolean {
  const t = text.trim();
  if (!t) return true;
  if (looksLikeFieldChrome(t)) return true;
  return (
    /\b(plan de trabajo|posible plan|voy a revisar|primero,? voy|necesito generar|identificar características|aquí (es|está) un posible|\*\*identificar|ayudar a renata|características del producto)\b/i.test(
      t,
    ) ||
    /^\s*\d+\.\s+\*\*/m.test(t) ||
    ((t.match(/^\s*\d+\./gm) ?? []).length >= 3) ||
    /us[aá] «aplicar/i.test(t)
  );
}

export function sanitizeChatAssistantContent(content: string): string {
  return stripProposalMarkup(stripFieldDraft(content))
    .replace(/\n*Us[aá] «Aplicar[\s\S]*$/i, "")
    .replace(/\n*Tu siguiente paso[\s\S]*$/i, "")
    .replace(/\n*Mi siguiente paso[\s\S]*$/i, "")
    .trim();
}

/**
 * Strip chat chrome and keep only the copy that belongs in the form field.
 * Prefers """...""" / ```...``` bodies when the model wraps the draft.
 */
export function cleanFieldDraftValue(raw: string): string | null {
  let text = sanitizeChatAssistantContent(String(raw ?? "")).trim();
  if (!text) return null;

  const triple = text.match(/"""([\s\S]*?)"""/);
  if (triple?.[1] && triple[1].trim().length >= 20) {
    text = triple[1].trim();
  } else {
    const md = text.match(/```(?:[\w-]+)?\n([\s\S]*?)```/);
    if (md?.[1] && md[1].trim().length >= 20) {
      text = md[1].trim();
    }
  }

  text = text
    .replace(/^Estás editando el campo[\s\S]*?(?=\n\n|"""|```|$)/i, "")
    .replace(/^La descripci[oó]n propuesta es:\s*/i, "")
    .replace(/^Aqu[ií] (?:va|tenés|tienes) (?:la|el)[\s\S]{0,80}:\s*/i, "")
    .replace(/^Listo\.?\s*Prepar[eé] la [^:\n]{0,80}:\s*/i, "")
    .replace(/\n*Puedes revisarla[\s\S]*$/i, "")
    .replace(/\n*Pod[eé]s revisarla[\s\S]*$/i, "")
    .replace(/\n*Tu siguiente paso[\s\S]*$/i, "")
    .replace(/\n*Mi siguiente paso[\s\S]*$/i, "")
    .replace(/\n*Us[aá] «Aplicar[\s\S]*$/i, "")
    .replace(/\n*Listo: prepar[eé] el texto[\s\S]*$/i, "")
    .replace(/^"+|"+$/g, "")
    .trim();

  if (!text || text.length < 20) return null;
  if (looksLikeMetaPlanning(text) || looksLikeFieldChrome(text)) return null;
  if (/^Estás editando/i.test(text)) return null;
  if (/^Listo\.?\s*Prepar[eé]/i.test(text)) return null;
  return text;
}

/** Pull usable product copy from assistant prose when the model skipped FIELD_DRAFT. */
export function extractFieldDraftCandidate(
  assistantText: string,
  field: string,
): RenataFieldDraft | null {
  const direct = cleanFieldDraftValue(assistantText);
  if (direct && !looksLikeMetaPlanning(direct)) {
    return { field, value: direct };
  }

  let text = sanitizeChatAssistantContent(assistantText)
    .replace(/Tu siguiente paso[\s\S]*/i, "")
    .replace(/Mi siguiente paso[\s\S]*/i, "")
    .replace(/Estoy recordando…\n*/gi, "")
    .trim();
  if (!text || text.length < 40) return null;

  const paragraphs = text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => !META_LINE_RE.test(p))
    .filter((p) => !looksLikeMetaPlanning(p))
    .map((p) => cleanFieldDraftValue(p))
    .filter((p): p is string => Boolean(p && p.length >= 40 && !looksLikeMetaPlanning(p)));

  const candidate = paragraphs.sort((a, b) => b.length - a.length)[0] ?? null;
  if (!candidate || candidate.length < 40 || looksLikeMetaPlanning(candidate)) return null;
  return { field, value: candidate };
}
